import { useRichTextEditorContext } from '@mantine/tiptap';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import {
  Group,
  ColorPicker,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Code,
  Box,
  Tooltip,
  Menu,
  useMantineTheme,
  Divider,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import Button from 'components/Button';
import { useDisclosure } from '@mantine/hooks';
import DropdownControl, {
  DropdownControlKind,
} from 'pages/revisions-module/template-editor/editors/template/toolbar/controls/DropdownControl';
import { useTemplateEditorData } from 'pages/revisions-module/template-editor/TemplateEditorDataProvider';

/** Convert hex to RGB */
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

/** Convert RGB to hex */
const rgbToHex = (r: number, g: number, b: number) => {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

/** Convert RGB string to hex */
const RgbStrToHex = (rgb: string) => {
  const result = /^rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)$/.exec(rgb);
  if (result) {
    const r = parseInt(result[1], 10);
    const g = parseInt(result[2], 10);
    const b = parseInt(result[3], 10);
    return rgbToHex(r, g, b);
  }
  return '#000000';
};

/**
 * Control which allows the user to select text color.
 */
export default function Color({ kind }: { kind: DropdownControlKind }) {
  const theme = useMantineTheme();
  const { editor } = useRichTextEditorContext();
  const [currentColor, setCurrentColor] = useState('#000000'); // Applied color in editor
  const [tempColor, setTempColor] = useState('#000000'); // Temporary color being edited
  const [tempRgbValues, setTempRgbValues] = useState({ r: 0, g: 0, b: 0 });
  const [tempHexInput, setTempHexInput] = useState('#000000');
  const [dropdownOpened] = useDisclosure(false);
  const { visual } = useTemplateEditorData();

  // Get current color from editor selection
  useEffect(() => {
    if (editor) {
      const color = editor.getAttributes('textStyle').color;
      if (color) {
        setCurrentColor(color);
        setTempColor(color);
        setTempHexInput(color);
        updateTempRgbFromHex(color);
      } else {
        const hex = RgbStrToHex(visual.textColor);
        setCurrentColor(hex);
        setTempColor(hex);
        setTempHexInput(hex);
        setTempRgbValues(hexToRgb(hex));
      }
    }
  }, [editor?.state.selection, visual.textColor]);

  // Prevent editor focus when dropdown is opened
  useEffect(() => {
    if (dropdownOpened && editor) {
      // Blur the editor to prevent keyboard shortcuts
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      editor.commands.blur();
    }
  }, [editor, dropdownOpened]);

  /** Update temporary RGB values from hex color */
  const updateTempRgbFromHex = (hex: string) => {
    const rgb = hexToRgb(hex);
    setTempRgbValues(rgb);
  };

  /** Apply color using extension commands */
  const applyColor = (color: string) => {
    setCurrentColor(color);
    setTempColor(color);
    setTempHexInput(color);
    updateTempRgbFromHex(color);
    editor?.chain().focus().setColor(color).run();
  };

  /** Apply temporary color to editor */
  const handleApply = (event: React.MouseEvent) => {
    applyColor(tempColor);
  };

  /** Cancel changes and revert to current color */
  const handleCancel = () => {
    // Revert to current color
    setTempColor(currentColor);
    setTempHexInput(currentColor);
    updateTempRgbFromHex(currentColor);
    // Dropdown will close naturally due to closeOnClick=true
  };

  /** Copy color to clipboard */
  const copyColorToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentColor);
      notifications.show({
        title: 'Skopírované!',
        message: `Farba ${currentColor} bola skopírovaná do schránky`,
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Chyba',
        message: 'Nepodarilo sa skopírovať farbu do schránky',
        color: 'red',
      });
    }
  };

  /** Handle hex input change */
  const handleHexInputChange = (value: string) => {
    setTempHexInput(value);
    // Update temp color if valid hex
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (hexRegex.test(value)) {
      setTempColor(value);
      updateTempRgbFromHex(value);
    }
  };

  /** Handle color picker change */
  const handleColorPickerChange = (color: string) => {
    setTempColor(color);
    setTempHexInput(color);
    updateTempRgbFromHex(color);
  };

  /** Handle RGB input change */
  const handleRgbChange = (component: 'r' | 'g' | 'b', value: number | '') => {
    if (typeof value === 'number') {
      const clampedValue = Math.max(0, Math.min(255, value));
      const newRgb = { ...tempRgbValues, [component]: clampedValue };
      setTempRgbValues(newRgb);
      // Update temp color and hex input
      const hex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
      setTempColor(hex);
      setTempHexInput(hex);
    }
  };

  return (
    <DropdownControl
      kind={kind}
      label={`Farba textu (${currentColor})`}
      // closeOnClick={true}
      hideChevron
      icon={
        <div
          style={{
            width: '16px',
            height: '16px',
            backgroundColor: currentColor,
            border: '1px solid #ccc',
            borderRadius: '2px',
          }}
        />
      }
    >
      <Stack spacing="sm" p="md" miw={300}>
        {/* Color Picker */}
        <div onClick={(e) => e.stopPropagation()}>
          <ColorPicker
            value={tempColor}
            onChange={handleColorPickerChange}
            size="sm"
            w="100%"
            swatches={[
              '#000000',
              theme.colors.gray[9],
              theme.colors.gray[8],
              theme.colors.gray[7],
              theme.colors.gray[6],
              theme.colors.red[7],
              theme.colors.orange[7],
              theme.colors.yellow[7],
              theme.colors.blue[7],
              theme.colors.green[7],
            ]}
          />
        </div>

        {/* RGB Inputs */}
        <Group spacing={8} onClick={(e) => e.stopPropagation()}>
          <NumberInput
            label="R"
            value={tempRgbValues.r}
            onChange={(value) => handleRgbChange('r', value)}
            min={0}
            max={255}
            size="xs"
            w={48}
          />
          <NumberInput
            label="G"
            value={tempRgbValues.g}
            onChange={(value) => handleRgbChange('g', value)}
            min={0}
            max={255}
            size="xs"
            w={48}
          />
          <NumberInput
            label="B"
            value={tempRgbValues.b}
            onChange={(value) => handleRgbChange('b', value)}
            min={0}
            max={255}
            size="xs"
            w={48}
          />
          <TextInput
            label="HEX"
            value={tempHexInput}
            onChange={(event) => handleHexInputChange(event.currentTarget.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder="#000000"
            size="xs"
            w={100}
          />
        </Group>

        {/* Current color display */}
        <Divider color="gray.2"></Divider>

        <Group spacing="xs" align="center" noWrap>
          <Text size="xs">Aktuálna&nbsp;farba:</Text>
          <Tooltip label="Kliknite pre skopírovanie">
            <Code
              onClick={copyColorToClipboard}
              style={{
                cursor: 'pointer',
                padding: '2px 6px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontWeight: 500,
              }}
            >
              {currentColor}
            </Code>
          </Tooltip>
          <Box
            style={{
              width: '100%',
              height: '24px',
              backgroundColor: currentColor,
              border: '2px solid #dee2e6',
              borderRadius: '6px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
          />
        </Group>

        <Divider color="gray.2"></Divider>

        {/* Action buttons */}
        <Group spacing="xs" grow>
          <Menu.Item style={{ padding: 0, flex: 1, borderRadius: '4px' }} onClick={handleCancel}>
            <Button size="xs" variant="outline" color="gray" w="100%" leftIcon={<IconX size={14} />}>
              Zrušiť
            </Button>
          </Menu.Item>
          <Menu.Item onClick={handleApply} style={{ padding: 0, flex: 1, borderRadius: '4px' }}>
            <Button size="xs" variant="danger" w="100%" leftIcon={<IconCheck size={14} />}>
              Aplikovať
            </Button>
          </Menu.Item>
        </Group>
      </Stack>
    </DropdownControl>
  );
}
